/**
 * PinScreen.tsx
 *
 * Beautiful PIN entry screen for EmCog authentication.
 * Centered card with blurred background, matching the app's aesthetic.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './PinScreen.css';

interface PinScreenProps {
  emcogName?: string;
}

export default function PinScreen({ emcogName = 'EmCog' }: PinScreenProps) {
  const { verifyPin } = useAuth();
  const [pin, setPin] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Handle input change
  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullPin = newPin.join('');
      if (fullPin.length === 6) {
        handleSubmit(fullPin);
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newPin = [...pin];
        digits.forEach((digit, i) => {
          if (i < 6) newPin[i] = digit;
        });
        setPin(newPin);
        if (digits.length === 6) {
          handleSubmit(newPin.join(''));
        } else {
          inputRefs.current[digits.length]?.focus();
        }
      });
    }
  };

  // Handle paste on input
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '').slice(0, 6).split('');
    const newPin = ['', '', '', '', '', ''];
    digits.forEach((digit, i) => {
      if (i < 6) newPin[i] = digit;
    });
    setPin(newPin);
    if (digits.length === 6) {
      handleSubmit(newPin.join(''));
    } else {
      inputRefs.current[digits.length]?.focus();
    }
  };

  // Submit PIN
  const handleSubmit = async (fullPin: string) => {
    if (fullPin.length !== 6) return;

    setIsVerifying(true);
    setError(null);

    const result = await verifyPin(fullPin);

    if (!result.success) {
      setError(result.error || 'Invalid PIN');
      setShake(true);
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();

      // Remove shake after animation
      setTimeout(() => setShake(false), 500);
    }

    setIsVerifying(false);
  };

  return (
    <div className="pin-screen">
      {/* Animated background */}
      <div className="pin-background">
        <div className="pin-orb pin-orb-1"></div>
        <div className="pin-orb pin-orb-2"></div>
        <div className="pin-orb pin-orb-3"></div>
      </div>

      {/* Blurred overlay */}
      <div className="pin-overlay"></div>

      {/* PIN Card */}
      <div className={`pin-card ${shake ? 'shake' : ''}`}>
        {/* Logo/Icon */}
        <div className="pin-logo">
          <div className="pin-logo-inner">
            <svg viewBox="0 0 100 100" className="pin-logo-svg">
              <defs>
                <linearGradient id="pinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#5b6af5" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="none" stroke="url(#pinGradient)" strokeWidth="2" opacity="0.3" />
              <circle cx="50" cy="50" r="35" fill="none" stroke="url(#pinGradient)" strokeWidth="2" opacity="0.5" />
              <circle cx="50" cy="50" r="25" fill="url(#pinGradient)" opacity="0.8" />
              <circle cx="50" cy="50" r="15" fill="white" opacity="0.9" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h1 className="pin-title">{emcogName}</h1>
        <p className="pin-subtitle">Enter your PIN to connect</p>

        {/* PIN Input */}
        <div className="pin-inputs">
          {pin.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={`pin-input ${digit ? 'filled' : ''} ${error ? 'error' : ''}`}
              disabled={isVerifying}
              autoComplete="off"
            />
          ))}
        </div>

        {/* Error message */}
        {error && (
          <p className="pin-error">{error}</p>
        )}

        {/* Loading state */}
        {isVerifying && (
          <div className="pin-loading">
            <div className="pin-spinner"></div>
            <span>Connecting...</span>
          </div>
        )}

        {/* Help text */}
        <p className="pin-help">
          This device will be remembered for 30 days
        </p>
      </div>
    </div>
  );
}
